import {
  createContext,
  type JSX,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { installBrowserStateBroadcast } from "../state/browserState";

interface BootRefreshValue {
  token: number;
  requestRefresh: () => void;
}

const BootRefreshContext = createContext<BootRefreshValue | null>(null);

/**
 * Interim refresh channel until the query layer owns invalidation: bumping
 * the token re-runs every mounted boot read, and cross-tab refresh
 * broadcasts from browser-state reconciliation feed the same channel.
 */
export function BootRefreshProvider({ children }: { children: ReactNode }): JSX.Element {
  const [token, setToken] = useState(0);
  const requestRefresh = useCallback(() => setToken((value) => value + 1), []);
  useEffect(() => installBrowserStateBroadcast(requestRefresh), [requestRefresh]);
  const value = useMemo(() => ({ token, requestRefresh }), [token, requestRefresh]);
  return <BootRefreshContext.Provider value={value}>{children}</BootRefreshContext.Provider>;
}

export function useBootRefresh(): BootRefreshValue {
  const value = useContext(BootRefreshContext);
  if (!value) {
    throw new Error("useBootRefresh requires BootRefreshProvider above the router");
  }
  return value;
}
